import { useState } from 'react'
import { Download, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/Button'
import { apiJson } from '@/api/client'
import { downloadAuthed } from '@/lib/download'
import { classifyApiError } from '@/lib/api-error'
import { buildExportFilters, invalidExportAssignees } from '../../api'
import type { AlertFilters, ExportJob, SortKey } from '../../types'

interface Props {
  filters: AlertFilters
  sort: SortKey[]
}

/**
 * AF-126 — async CSV export.
 *
 * Posts the current filter set, polls the returned job ID until status is
 * `ready`, then drops the signed download URL into a Sonner toast as an
 * actionable link. Synchronous download breaks at 10k+ rows so we never
 * try the inline route.
 */
export function ExportCsvButton({ filters, sort }: Props) {
  const [busy, setBusy] = useState(false)

  const start = async () => {
    if (invalidExportAssignees(filters).length > 0) {
      toast.error('Export blocked: the assignee filter contains an invalid user. Clear it and try again.')
      return
    }
    setBusy(true)
    const toastId = toast.loading('Preparing CSV…')
    try {
      const job = await apiJson<ExportJob>('/alerts/export', {
        method: 'POST',
        body: { filters: buildExportFilters(filters), sort: sort.map((s) => `${String(s.field)}:${s.dir}`) },
      })
      const ready = await pollUntilReady(job.id)
      toast.success(`CSV ready · ${ready.row_count.toLocaleString()} rows`, {
        id: toastId,
        action: ready.download_url
          ? {
              label: 'Download',
              onClick: () => {
                void downloadAuthed(ready.download_url!, `alerts-export-${ready.id}.csv`)
                  .catch((err: unknown) => {
                    toast.error(err instanceof Error ? err.message : 'Download failed')
                  })
              },
            }
          : undefined,
        duration: 30_000,
      })
    } catch (err) {
      toast.error(exportErrorMessage(err), { id: toastId })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Button variant="outline" size="sm" onClick={() => void start()} disabled={busy}>
      {busy ? (
        <Loader2 size={14} className="animate-spin" />
      ) : (
        <Download size={14} />
      )}
      Export CSV
    </Button>
  )
}

/**
 * 4xx messages are server-authored and safe to show (classifyApiError drops
 * 5xx text). A 400 invalid_filter carries the specific rejected filter.
 */
function exportErrorMessage(err: unknown): string {
  const f = classifyApiError(err)
  if (f.kind === 'forbidden') return 'You do not have permission to export alerts.'
  if (f.kind === 'client' && f.serverMessage) {
    return f.code === 'invalid_filter'
      ? `Export filters were rejected: ${f.serverMessage}`
      : f.serverMessage
  }
  if (f.kind === 'server') return 'CSV export failed on the server. Try again shortly.'
  return err instanceof Error ? err.message : 'CSV export failed'
}

async function pollUntilReady(jobId: string, timeoutMs = 60_000): Promise<ExportJob> {
  const start = Date.now()
  for (;;) {
    const job = await apiJson<ExportJob>(`/alerts/export/${jobId}`)
    if (job.status === 'ready') return job
    if (job.status === 'failed') throw new Error(job.error ?? 'Export failed')
    if (Date.now() - start > timeoutMs) throw new Error('Export timed out')
    await new Promise((r) => setTimeout(r, 700))
  }
}

